import { useQuery } from "@tanstack/react-query";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export const useVideo = (id?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      if (!id) throw new Error("Missing videoId");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].video[":videoId"].$get({
            param: {
              ...scope.param,
              videoId: id,
            },
          })
        : await client.merchant[":merchantId"].video[":videoId"].$get({
            param: {
              ...scope.param,
              videoId: id,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!id,
  });
};

export const useVideoPerformance = (id?: string) => {
  const scope = useScopedMerchantClient();

  return useQuery({
    queryKey: ["video", "performance", scope.merchantId, scope.workspaceId, id],
    queryFn: async () => {
      if (!id) throw new Error("Missing videoId");
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"].video[":videoId"].performance.$get({
            param: {
              ...scope.param,
              videoId: id,
            },
          })
        : await client.merchant[":merchantId"].video[":videoId"].performance.$get({
            param: {
              ...scope.param,
              videoId: id,
            },
          });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!scope.merchantId && !!id,
  });
};

const VIDEOS_BATCH_CHUNK_SIZE = 200;

export const useVideosByIds = (videoIds: string[]) => {
  const scope = useScopedMerchantClient();
  const uniqueIds = Array.from(new Set(videoIds)).sort();

  return useQuery({
    queryKey: ["videos-batch", scope.merchantId, scope.workspaceId, uniqueIds],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < uniqueIds.length; i += VIDEOS_BATCH_CHUNK_SIZE) {
        chunks.push(uniqueIds.slice(i, i + VIDEOS_BATCH_CHUNK_SIZE));
      }

      const results = await Promise.all(
        chunks.map(async (chunk) => {
          const res = scope.workspaceId
            ? await client.workspace[":workspaceId"].merchant[":merchantId"].video.batch.$post({
                param: scope.param,
                json: { videoIds: chunk },
              })
            : await client.merchant[":merchantId"].video.batch.$post({
                param: scope.param,
                json: { videoIds: chunk },
              });
          if (!res.ok) throw await handleError(res);
          return res.json();
        }),
      );

      return Object.assign({}, ...results) as (typeof results)[0];
    },
    enabled: !!scope.merchantId && uniqueIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
};
